import { ReactNode, useMemo, useRef, useState } from 'react';
import {
  autoUpdate,
  flip,
  FloatingFocusManager,
  FloatingPortal,
  limitShift,
  offset,
  shift,
  size,
  useDismiss,
  useFloating,
  useInteractions,
  useListNavigation,
  useRole,
} from '@floating-ui/react';
import { useEditorEffect } from '@handlewithcare/react-prosemirror';
import { observer } from 'mobx-react-lite';
import { Box } from '@mantine/core';
import { FloatingMenuContext } from './store';
import { getSelectionRect } from './util';
import classes from './FloatingMenu.module.css';

export interface FloatingMenuProps {
  children: ReactNode;
  onOpenChange: (opened: boolean) => void;
}

export const FloatingMenu = observer(({ children, onOpenChange }: FloatingMenuProps) => {
  const virtualItemRef = useRef<HTMLButtonElement | null>(null);
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const listRef = useRef<Array<HTMLButtonElement>>([]);

  const { refs, floatingStyles, context } = useFloating({
    open: true,
    placement: 'bottom-start',
    onOpenChange,
    whileElementsMounted: autoUpdate,
    middleware: [
      offset(0),
      shift({ limiter: limitShift() }),
      flip({ padding: 5 }),
      size({
        padding: 5,
        apply({ availableHeight }) {
          const styles = refs.floating.current?.style ?? {};
          Object.assign(styles, {
            maxHeight: `${availableHeight}px`,
          });
        },
      }),
    ],
  });

  const listNavigation = useListNavigation(context, {
    listRef,
    activeIndex,
    onNavigate: setActiveIndex,
    loop: true,
    virtual: true,
    virtualItemRef,
  });

  const role = useRole(context, { role: 'listbox' });
  const dismiss = useDismiss(context);

  const { getReferenceProps, getFloatingProps, getItemProps } = useInteractions([
    role,
    dismiss,
    listNavigation,
  ]);

  useEditorEffect((view) => {
    const clientRect = getSelectionRect(view);

    refs.setPositionReference({
      getBoundingClientRect: () => clientRect,
    });

    const { onKeyDown } = getReferenceProps();

    function onKeyDownEditor(e: KeyboardEvent) {
      if (e.key === 'Enter') {
        e.preventDefault();
        e.stopPropagation();
        virtualItemRef.current?.click();
      }

      if (typeof onKeyDown === 'function') {
        onKeyDown(e);
      }
    }

    view.dom.addEventListener('keydown', onKeyDownEditor);

    return () => {
      view.dom.removeEventListener('keydown', onKeyDownEditor);
    };
  }, []);

  const floatingMenuContext = useMemo(
    () => ({
      context,
      refs,
      getReferenceProps,
      getItemProps,
      activeIndex,
      listRef,
    }),
    [context, refs, getReferenceProps, getItemProps, activeIndex]
  );

  return (
    <FloatingMenuContext.Provider value={floatingMenuContext}>
      <FloatingPortal>
        <FloatingFocusManager context={context} modal={false} initialFocus={-1}>
          <Box
            ref={refs.setFloating}
            style={floatingStyles}
            {...getFloatingProps({
              className: classes.floatingMenu,
            })}
          >
            {children}
          </Box>
        </FloatingFocusManager>
      </FloatingPortal>
    </FloatingMenuContext.Provider>
  );
});
